import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Typography, Divider, Collapse
} from '@mui/material';
import {
  Dashboard, Inventory, People, ShoppingCart, Receipt, Badge,
  AccountBalance, MenuBook, Assessment, LocalShipping, Store,
  Inbox, Settings, ExpandLess, ExpandMore, Category,
  RequestQuote, PointOfSale, Warehouse
} from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';

const DRAWER_WIDTH = 260;

interface MenuItem { labelKey: string; icon: React.ReactNode; path: string; }
interface MenuModule { labelKey: string; icon: React.ReactNode; items?: MenuItem[]; path?: string; }

const menuModules: MenuModule[] = [
  { labelKey: 'nav.dashboard', icon: <Dashboard />, path: '/dashboard' },
  {
    labelKey: 'nav.accounts_receivable', icon: <PointOfSale />,
    items: [
      { labelKey: 'nav.sales_orders', icon: <ShoppingCart />, path: '/sales-orders' },
      { labelKey: 'nav.invoices', icon: <Receipt />, path: '/invoices' },
      { labelKey: 'nav.customers', icon: <People />, path: '/customers' },
    ]
  },
  {
    labelKey: 'nav.accounts_payable', icon: <RequestQuote />,
    items: [
      { labelKey: 'nav.purchase_orders', icon: <LocalShipping />, path: '/purchase-orders' },
      { labelKey: 'nav.purchase_invoices', icon: <Receipt />, path: '/purchase-invoices' },
      { labelKey: 'nav.suppliers', icon: <Store />, path: '/suppliers' },
      { labelKey: 'nav.goods_receipts', icon: <Inbox />, path: '/goods-receipts' },
    ]
  },
  {
    labelKey: 'nav.ledger', icon: <AccountBalance />,
    items: [
      { labelKey: 'nav.chart_of_accounts', icon: <AccountBalance />, path: '/accounts' },
      { labelKey: 'nav.journal_entries', icon: <MenuBook />, path: '/journal-entries' },
      { labelKey: 'posting.title', icon: <Assessment />, path: '/posting-profiles' },
      { labelKey: 'subledger.title', icon: <MenuBook />, path: '/subledger' },
      { labelKey: 'nav.financial_reports', icon: <Assessment />, path: '/reports' },
    ]
  },
  {
    labelKey: 'nav.product_management', icon: <Category />,
    items: [
      { labelKey: 'nav.products', icon: <Inventory />, path: '/products' },
    ]
  },
  { labelKey: 'nav.inventory', icon: <Warehouse />, items: [] },
  {
    labelKey: 'nav.hr', icon: <Badge />,
    items: [
      { labelKey: 'nav.employees', icon: <Badge />, path: '/employees' },
    ]
  },
  { labelKey: 'nav.settings', icon: <Settings />, path: '/settings' },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [openModules, setOpenModules] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    menuModules.forEach(mod => {
      if (mod.items?.some(item => location.pathname.startsWith(item.path))) {
        initial[mod.labelKey] = true;
      }
    });
    return initial;
  });

  const toggleModule = (key: string) => setOpenModules(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <Drawer variant="permanent" sx={{
      width: DRAWER_WIDTH, flexShrink: 0,
      '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
    }}>
      <Toolbar><Typography variant="h6" noWrap>ERP System</Typography></Toolbar>
      <Divider />
      <List dense sx={{ pt: 0 }}>
        {menuModules.map((mod) => {
          if (mod.path) {
            return (
              <ListItem key={mod.labelKey} disablePadding>
                <ListItemButton selected={location.pathname.startsWith(mod.path)} onClick={() => navigate(mod.path!)} sx={{ py: 0.8 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>{mod.icon}</ListItemIcon>
                  <ListItemText primary={t(mod.labelKey)} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                </ListItemButton>
              </ListItem>
            );
          }
          const isOpen = openModules[mod.labelKey] || false;
          const hasItems = mod.items && mod.items.length > 0;
          const isModuleActive = mod.items?.some(item => location.pathname.startsWith(item.path));
          return (
            <React.Fragment key={mod.labelKey}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => hasItems ? toggleModule(mod.labelKey) : undefined}
                  sx={{ py: 0.5, bgcolor: isModuleActive ? 'action.selected' : undefined }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>{mod.icon}</ListItemIcon>
                  <ListItemText primary={t(mod.labelKey)}
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isModuleActive ? 600 : 500, color: !hasItems ? 'text.disabled' : undefined }} />
                  {hasItems && (isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
                </ListItemButton>
              </ListItem>
              {hasItems && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List dense disablePadding>
                    {mod.items!.map((item) => (
                      <ListItem key={item.path} disablePadding>
                        <ListItemButton selected={location.pathname.startsWith(item.path)} onClick={() => navigate(item.path)} sx={{ pl: 5, py: 0.4 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>{item.icon}</ListItemIcon>
                          <ListItemText primary={t(item.labelKey)} primaryTypographyProps={{ fontSize: '0.82rem' }} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
